import { config } from "../../../config/config";
import type {
  Gym,
  GymDetail,
  CreateGymInput,
  UpdateGymInput,
  CreateAdminPaymentMethodInput,
  UpdateAdminPaymentMethodInput,
  GymPaymentMethod,
  GymPlanCategory,
  CreateAdminPlanCategoryInput,
  UpdateAdminPlanCategoryInput,
  GymEmailConfig,
  UpdateGymEmailConfigInput,
} from "../../../utils/zod/admin";
import type { ModulesSettings, BusinessType } from "../../../utils/zod/gym";
import type {
  GymExpensesConfig,
  UpdateGymExpensesConfigInput,
} from "../../../utils/zod/expensesConfig";

export const getAllGymsRequest = async (token: string): Promise<Gym[]> => {
  const response = await fetch(`${config.API_URL}/admin/gyms`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorMessage = "Error al obtener los negocios";

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
    } else {
      errorMessage = response.statusText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data;
};

export const createGymRequest = async (
  gym: CreateGymInput,
  token: string,
): Promise<Gym> => {
  const response = await fetch(`${config.API_URL}/admin/gyms`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(gym),
  });

  if (!response.ok) {
    let errorMessage = "Error al crear el negocio";

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
    } else {
      errorMessage = response.statusText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data;
};

export const getCurrentUserGymEmailConfigRequest = async (
  token: string,
): Promise<GymEmailConfig> => {
  const response = await fetch(`${config.API_URL}/v1/gym/email-config`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorMessage =
      "Error al obtener la configuración de emails del negocio";

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
    } else {
      errorMessage = response.statusText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data;
};

export const getGymDetailRequest = async (
  publicId: string,
  token: string,
): Promise<GymDetail> => {
  const response = await fetch(`${config.API_URL}/admin/gyms/${publicId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || response.statusText || "Error al obtener el negocio",
    );
  }

  return response.json();
};

export const updateGymRequest = async (
  publicId: string,
  input: UpdateGymInput,
  token: string,
): Promise<Gym> => {
  const response = await fetch(`${config.API_URL}/admin/gyms/${publicId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al actualizar el negocio",
    );
  }

  return response.json();
};

export const createAdminPaymentMethodRequest = async (
  gymPublicId: string,
  input: CreateAdminPaymentMethodInput,
  token: string,
): Promise<GymPaymentMethod> => {
  const response = await fetch(
    `${config.API_URL}/admin/gyms/${gymPublicId}/payment-methods`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al crear el método de pago del negocio",
    );
  }

  return response.json();
};

export const updateAdminPaymentMethodRequest = async (
  gymPublicId: string,
  pmPublicId: string,
  input: UpdateAdminPaymentMethodInput,
  token: string,
): Promise<GymPaymentMethod> => {
  const response = await fetch(
    `${config.API_URL}/admin/gyms/${gymPublicId}/payment-methods/${pmPublicId}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al actualizar el método de pago",
    );
  }

  return response.json();
};

export const deleteAdminPaymentMethodRequest = async (
  gymPublicId: string,
  pmPublicId: string,
  token: string,
): Promise<void> => {
  const response = await fetch(
    `${config.API_URL}/admin/gyms/${gymPublicId}/payment-methods/${pmPublicId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al eliminar el método de pago",
    );
  }
};

export interface PaymentMethodPreset {
  key: string;
  name: string;
  type: "manual";
  ticketConfig: {
    plan: { requires: boolean };
    food: { requires: boolean };
    merchandise: { requires: boolean };
  };
}

export interface ApplyPaymentMethodPresetsResult {
  created: GymPaymentMethod[];
  skipped: { key: string; reason: "already_exists" }[];
}

export const getPaymentMethodPresetsRequest = async (
  token: string,
): Promise<PaymentMethodPreset[]> => {
  const response = await fetch(
    `${config.API_URL}/admin/payment-method-presets`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al cargar las plantillas de método de pago",
    );
  }

  return response.json();
};

export const applyPaymentMethodPresetsRequest = async (
  gymPublicId: string,
  presetKeys: string[],
  token: string,
): Promise<ApplyPaymentMethodPresetsResult> => {
  const response = await fetch(
    `${config.API_URL}/admin/gyms/${gymPublicId}/payment-methods/from-presets`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ presetKeys }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al aplicar las plantillas",
    );
  }

  return response.json();
};

export const updateCurrentUserGymEmailConfigRequest = async (
  input: UpdateGymEmailConfigInput,
  token: string,
): Promise<GymEmailConfig> => {
  const response = await fetch(`${config.API_URL}/v1/gym/email-config`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    let errorMessage = "Error al actualizar la configuración de emails";

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
    } else {
      errorMessage = response.statusText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data;
};

export const createAdminPlanCategoryRequest = async (
  gymPublicId: string,
  input: CreateAdminPlanCategoryInput,
  token: string,
): Promise<GymPlanCategory> => {
  const response = await fetch(
    `${config.API_URL}/admin/gyms/${gymPublicId}/plan-categories`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al crear la categoría de plan",
    );
  }

  return response.json();
};

export const getGymClientFieldsConfigRequest = async (
  gymPublicId: string,
  token: string,
): Promise<Record<string, { status: string; label: string }>> => {
  const response = await fetch(
    `${config.API_URL}/admin/gyms/${gymPublicId}/client-fields-config`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al obtener la configuración de campos",
    );
  }

  return response.json();
};

export const updateGymClientFieldsConfigRequest = async (
  gymPublicId: string,
  input: Record<string, { status?: string; label?: string }>,
  token: string,
): Promise<Record<string, { status: string; label: string }>> => {
  const response = await fetch(
    `${config.API_URL}/admin/gyms/${gymPublicId}/client-fields-config`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al actualizar la configuración de campos",
    );
  }

  return response.json();
};

export const updateAdminPlanCategoryRequest = async (
  gymPublicId: string,
  categoryPublicId: string,
  input: UpdateAdminPlanCategoryInput,
  token: string,
): Promise<GymPlanCategory> => {
  const response = await fetch(
    `${config.API_URL}/admin/gyms/${gymPublicId}/plan-categories/${categoryPublicId}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al actualizar la categoría de plan",
    );
  }

  return response.json();
};

export const deleteAdminPlanCategoryRequest = async (
  gymPublicId: string,
  categoryPublicId: string,
  token: string,
): Promise<void> => {
  const response = await fetch(
    `${config.API_URL}/admin/gyms/${gymPublicId}/plan-categories/${categoryPublicId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al eliminar la categoría de plan",
    );
  }
};

export const updateGymModulesConfigRequest = async (
  gymPublicId: string,
  input: Partial<ModulesSettings>,
  token: string,
): Promise<ModulesSettings> => {
  const response = await fetch(
    `${config.API_URL}/admin/gyms/${gymPublicId}/modules-config`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al actualizar la configuración de módulos",
    );
  }

  return response.json();
};

export interface ProviderConfigStatus {
  provider: string;
  configured: boolean;
  configuredAt: string | null;
  configuredBy: string | null;
  webhookUrl: string | null;
}

export interface GatewayConfigResponse {
  providers: ProviderConfigStatus[];
}

export const getGymGatewayConfigRequest = async (
  gymPublicId: string,
  token: string,
): Promise<GatewayConfigResponse> => {
  const response = await fetch(
    `${config.API_URL}/admin/gyms/${gymPublicId}/gateway-config`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al obtener la configuración del gateway",
    );
  }

  return response.json();
};

export const getGymBusinessTypeRequest = async (
  gymPublicId: string,
  token: string,
): Promise<BusinessType> => {
  const response = await fetch(
    `${config.API_URL}/admin/gyms/${gymPublicId}/business-type`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al obtener el tipo de negocio",
    );
  }

  const data = await response.json();
  return (typeof data === "string" ? data : data.businessType) as BusinessType;
};

export const updateGymBusinessTypeRequest = async (
  gymPublicId: string,
  businessType: BusinessType,
  token: string,
): Promise<BusinessType> => {
  const response = await fetch(
    `${config.API_URL}/admin/gyms/${gymPublicId}/business-type`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ businessType }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al actualizar el tipo de negocio",
    );
  }

  const data = await response.json();
  return (typeof data === "string" ? data : data.businessType) as BusinessType;
};

export const getGymModulesConfigRequest = async (
  gymPublicId: string,
  token: string,
): Promise<ModulesSettings> => {
  const response = await fetch(
    `${config.API_URL}/admin/gyms/${gymPublicId}/modules-config`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al obtener la configuración de módulos",
    );
  }

  return response.json();
};

export const getCurrentUserGymExpensesConfigRequest = async (
  token: string,
): Promise<GymExpensesConfig> => {
  const response = await fetch(`${config.API_URL}/admin/expenses-config`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al obtener la configuración de gastos",
    );
  }

  return response.json();
};

export const updateCurrentUserGymExpensesConfigRequest = async (
  input: UpdateGymExpensesConfigInput,
  token: string,
): Promise<GymExpensesConfig> => {
  const response = await fetch(`${config.API_URL}/admin/expenses-config`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        response.statusText ||
        "Error al actualizar la configuración de gastos",
    );
  }

  return response.json();
};
